import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_DIRECTION_ASC, TABLE_DIRECTION_DESC } from 'lib/tableLib'
import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import useSnackbar from '../Snackbar/useSnackbar'

const usePartnerInventoryFilters = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const defaultFilters = useMemo(
    () => ({
      searchQuery: '',
      plateQuery: '',
      vinQuery: '',
      sessionIdMin: 0,
      sessionIdMax: 0,
      daysMin: 0,
      daysMax: 0,
      leadMin: 0,
      leadMax: 0,
      priceMin: 0,
      priceMax: 0,
      assignedAtFrom: null,
      assignedAtTo: null,
      stateChangedAtFrom: null,
      stateChangedAtTo: null,
      partners: [OPTION_ALL_VALUE],
      brands: [OPTION_ALL_VALUE],
      models: [OPTION_ALL_VALUE],
      channels: [OPTION_ALL_VALUE],
      states: [OPTION_ALL_VALUE],
      order: { column: 'sessionId', direction: TABLE_DIRECTION_DESC },
      commentsQuery: '',
    }),
    []
  )

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    error: null,
  })
  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    sessionIdMin: 0,
    sessionIdMax: 0,
    daysMin: 0,
    daysMax: 0,
    leadMin: 0,
    leadMax: 0,
    priceMin: 0,
    priceMax: 0,
    partners: [allOption],
    brands: [allOption],
    models: [allOption],
    channels: [allOption],
    states: [allOption],
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error || null,
      }))
    },
    [setState]
  )

  const setSuccessFetching = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        error: null,
      }))

      setFiltersData((s) => ({
        ...s,
        ...data,
        partners: [
          allOption,
          ...data?.partners?.map((partner) =>
            getOption(partner.name, partner.id)
          ),
        ],
        brands: [allOption, ...data?.brands.map(getOption)],
        models: [allOption, ...data?.models.map(getOption)],
        channels: [
          allOption,
          ...(data?.channels
            ? data?.channels?.map((channel) =>
                getOption(t(`partnerInventory.channel.${channel}`), channel)
              )
            : []),
        ],
        states: [
          allOption,
          ...data?.states?.map((state) =>
            getOption(t(`partnerInventory.status.${state}`), state)
          ),
        ],
      }))

      // Preserviamo i filtri esistenti se è già inizializzato
      if (filters.initialized) {
        setFilters((currentFilters) => ({
          ...currentFilters,
          sessionIdMin:
            data.sessionIdMin < currentFilters.sessionIdMin
              ? data.sessionIdMin
              : currentFilters.sessionIdMin,
          sessionIdMax:
            data.sessionIdMax > currentFilters.sessionIdMax
              ? data.sessionIdMax
              : currentFilters.sessionIdMax,
          daysMin:
            data.daysMin < currentFilters.daysMin
              ? data.daysMin
              : currentFilters.daysMin,
          daysMax:
            data.daysMax > currentFilters.daysMax
              ? data.daysMax
              : currentFilters.daysMax,
          leadMin:
            data.leadMin < currentFilters.leadMin
              ? data.leadMin
              : currentFilters.leadMin,
          leadMax:
            data.leadMax > currentFilters.leadMax
              ? data.leadMax
              : currentFilters.leadMax,
          priceMin:
            data.priceMin < currentFilters.priceMin
              ? data.priceMin
              : currentFilters.priceMin,
          priceMax:
            data.priceMax > currentFilters.priceMax
              ? data.priceMax
              : currentFilters.priceMax,
        }))
      } else {
        // Prima inizializzazione, imposta i valori predefiniti
        setFilters({
          ...defaultFilters,
          sessionIdMin: data.sessionIdMin,
          sessionIdMax: data.sessionIdMax,
          daysMin: data.daysMin,
          daysMax: data.daysMax,
          leadMin: data.leadMin,
          leadMax: data.leadMax,
          priceMin: data.priceMin,
          priceMax: data.priceMax,
          states: [...defaultFilters.states, ...data?.states],
          initialized: true,
        })
      }
    },
    [allOption, defaultFilters, t, filters]
  )

  const resetFilters = useCallback(() => {
    setFilters((s) => ({
      ...s,
      ...defaultFilters,
      sessionIdMin: filtersData.sessionIdMin,
      sessionIdMax: filtersData.sessionIdMax,
      daysMin: filtersData.daysMin,
      daysMax: filtersData.daysMax,
      leadMin: filtersData.leadMin,
      leadMax: filtersData.leadMax,
      priceMin: filtersData.priceMin,
      priceMax: filtersData.priceMax,
    }))
  }, [
    defaultFilters,
    filtersData.sessionIdMin,
    filtersData.sessionIdMax,
    filtersData.daysMin,
    filtersData.daysMax,
    filtersData.leadMin,
    filtersData.leadMax,
    filtersData.priceMin,
    filtersData.priceMax,
  ])

  const fetchFiltersData = useCallback(async () => {
    setStartFetching()

    try {
      const results = await axios.get(`${config.apiBaseUrl}filters/inventory`)
      setSuccessFetching(results.data.body)
    } catch (e) {
      let message =
        e.response?.data?.message ||
        t('common.error.fetchData', {
          attribute: e.response?.status,
        })

      setStopFetching(e)

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    }
  }, [
    setStartFetching,
    setStopFetching,
    setSuccessFetching,
    showSnackbarMessage,
    t,
  ])

  const initFilters = useCallback(async () => {
    await fetchFiltersData()
  }, [fetchFiltersData])

  const setSearchQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, searchQuery: value }))
    },
    [setFilters]
  )

  const setPlateQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, plateQuery: value }))
    },
    [setFilters]
  )

  const setVinQuery = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, vinQuery: value }))
    },
    [setFilters]
  )

  const setSessionIdRange = useCallback(
    (sessionIdMin, sessionIdMax) => {
      setFilters((s) => ({ ...s, sessionIdMin, sessionIdMax }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setBrands = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, brands: value }))
    },
    [setFilters]
  )

  const setModels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, models: value }))
    },
    [setFilters]
  )

  const setStates = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, states: value }))
    },
    [setFilters]
  )

  const setChannels = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, channels: value }))
    },
    [setFilters]
  )

  const setDaysRange = useCallback(
    (daysMin, daysMax) => {
      setFilters((s) => ({ ...s, daysMin, daysMax }))
    },
    [setFilters]
  )

  const setLeadRange = useCallback(
    (leadMin, leadMax) => {
      setFilters((s) => ({ ...s, leadMin, leadMax }))
    },
    [setFilters]
  )

  const setPriceRange = useCallback(
    (priceMin, priceMax) => {
      setFilters((s) => ({ ...s, priceMin, priceMax }))
    },
    [setFilters]
  )

  const setAssignedAtRange = useCallback(
    (assignedAtFrom, assignedAtTo) => {
      setFilters((s) => ({ ...s, assignedAtFrom, assignedAtTo }))
    },
    [setFilters]
  )

  const setStateChangedAtRange = useCallback(
    (stateChangedAtFrom, stateChangedAtTo) => {
      setFilters((s) => ({ ...s, stateChangedAtFrom, stateChangedAtTo }))
    },
    [setFilters]
  )

  const setSort = useCallback(
    (value) => {
      setFilters((s) => ({
        ...s,
        order: {
          column: value,
          direction:
            s.order.column !== value ||
            s.order.direction !== TABLE_DIRECTION_ASC
              ? TABLE_DIRECTION_ASC
              : TABLE_DIRECTION_DESC,
        },
      }))
    },
    [setFilters]
  )

  return {
    filters,
    filtersData,
    initFilters,
    setSearchQuery,
    setPlateQuery,
    setVinQuery,
    setSessionIdRange,
    setDaysRange,
    setLeadRange,
    setAssignedAtRange,
    setStateChangedAtRange,
    setPriceRange,
    setPartners,
    setBrands,
    setModels,
    setStates,
    setChannels,
    setSort,
    resetFilters,
    initialized: filters.initialized,
    isFetching: state.isFetching,
    error: state.error,
  }
}
export default usePartnerInventoryFilters
